import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import type { OrderStatus } from '~/graphql/graphql';
import { hasReachedStatus } from '~/utils';
import type { ContactDetails } from '~/components/cards/ContactCard';
import { contactLabel } from '~/utils/contact';

type OrderActorsProps = {
  status: Exclude<OrderStatus, 'DRAFT' | 'CANCELLED' | 'BLOCKED'>;
  isBrokered: boolean;
  confirmedBy: ContactDetails | null | undefined;
  shippedBy: ContactDetails | null | undefined;
};

function actorLabel(t: TFunction<'order'>, actor: ContactDetails): string {
  return t('order.actor', {
    name: contactLabel(actor),
    email: actor.externalId,
  });
}

export function OrderActors({
  status,
  isBrokered,
  confirmedBy,
  shippedBy,
}: Readonly<OrderActorsProps>) {
  const { t } = useTranslation('order');
  const showShipper = !isBrokered && hasReachedStatus(status, 'SHIPPED');

  if (!confirmedBy && !showShipper) {
    return null;
  }

  return (
    <div className="mt-4 space-y-1 text-sm text-neut-prim">
      {confirmedBy && (
        <p>{t('order.confirmed_by', { actor: actorLabel(t, confirmedBy) })}</p>
      )}
      {showShipper && (
        <p>
          {shippedBy
            ? t('order.shipped_by', { actor: actorLabel(t, shippedBy) })
            : t('order.shipper_not_recorded')}
        </p>
      )}
    </div>
  );
}
